
import React, { useState } from 'react'
import { Plus, Eye, Edit, Trash2, Users, Target, DollarSign, TrendingUp, Search, Filter } from 'lucide-react'
import { useLeads } from '../hooks/useLeads'
import MetricsCard from '../components/MetricsCard'
import LeadForm from '../components/LeadForm'
import LeadDetail from '../components/LeadDetail'

interface Lead {
  _id: string
  nome: string
  contato: string
  status: 'prospect' | 'negociacao' | 'perdido' | 'fechado'
  origem: string
  valor: number
  responsavel: string
  observacoes?: string
  createdAt: string
  updatedAt: string
}

const CRMDashboard: React.FC = () => {
  const { leads, metrics, loading, createLead, updateLead, deleteLead } = useLeads()
  const [showForm, setShowForm] = useState(false)
  const [editingLead, setEditingLead] = useState<Lead | null>(null)
  const [viewingLead, setViewingLead] = useState<Lead | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')

  const filteredLeads = leads.filter(lead => {
    const matchesSearch = lead.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         lead.contato.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         lead.responsavel.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesStatus = statusFilter === 'all' || lead.status === statusFilter
    return matchesSearch && matchesStatus
  })

  const handleCreateLead = async (leadData: Omit<Lead, '_id' | 'createdAt' | 'updatedAt'>) => {
    await createLead(leadData)
    setShowForm(false)
  }

  const handleEditLead = async (leadData: Omit<Lead, '_id' | 'createdAt' | 'updatedAt'>) => {
    if (editingLead) {
      // Editar lead existente
      await updateLead(editingLead._id, leadData)
      setEditingLead(null)
    }
  }

  const handleDeleteLead = async (leadId: string) => {
    if (confirm('Tem certeza que deseja excluir este lead?')) {
      await deleteLead(leadId)
    }
  }

  const handleStatusUpdate = async (leadId: string, newStatus: Lead['status']) => {
    await updateLead(leadId, { status: newStatus })
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'prospect': return 'bg-blue-100 text-blue-800'
      case 'negociacao': return 'bg-yellow-100 text-yellow-800'
      case 'fechado': return 'bg-green-100 text-green-800'
      case 'perdido': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'prospect': return 'Prospect'
      case 'negociacao': return 'Em Negociação'
      case 'fechado': return 'Fechado'
      case 'perdido': return 'Perdido'
      default: return status
    }
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value)
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Carregando dados do CRM...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">CRM Dashboard</h1>
              <p className="text-gray-600 mt-1">Gestão completa de leads e oportunidades</p>
            </div>
            <button
              onClick={() => setShowForm(true)}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 flex items-center gap-2 transition-colors"
            >
              <Plus size={20} />
              Novo Lead
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Métricas */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <MetricsCard
            title="Total de Leads"
            value={metrics.totalLeads}
            icon={Users}
            color="#3B82F6"
            description="Todos os leads cadastrados"
          />
          <MetricsCard
            title="Leads Fechados"
            value={metrics.leadsFechados}
            icon={Target}
            color="#10B981"
            description="Negócios concluídos"
          />
          <MetricsCard
            title="Valor Total Fechado"
            value={formatCurrency(metrics.valorTotalFechado)}
            icon={DollarSign}
            color="#059669"
            description="Receita confirmada"
          />
          <MetricsCard
            title="Valor em Negociação"
            value={formatCurrency(metrics.valorEmNegociacao)}
            icon={TrendingUp}
            color="#F59E0B"
            description="Pipeline de vendas"
          />
        </div>

        {/* Filtros e Busca */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Buscar leads por nome, contato ou responsável..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter size={20} className="text-gray-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">Todos os Status</option>
                <option value="prospect">Prospect</option>
                <option value="negociacao">Em Negociação</option>
                <option value="fechado">Fechado</option>
                <option value="perdido">Perdido</option>
              </select>
            </div>
          </div>
        </div>

        {/* Lista de Leads */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Lista de Leads ({filteredLeads.length})
            </h2>
          </div>

          {filteredLeads.length === 0 ? (
            <div className="text-center py-12">
              <Users className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhum lead encontrado</h3>
              <p className="mt-1 text-sm text-gray-500">
                {searchTerm || statusFilter !== 'all' 
                  ? 'Tente ajustar os filtros de busca'
                  : 'Comece cadastrando seu primeiro lead'
                }
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Nome
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contato
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Origem
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Valor
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Responsável
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredLeads.map((lead) => (
                    <tr key={lead._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{lead.nome}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{lead.contato}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(lead.status)}`}>
                          {getStatusLabel(lead.status)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{lead.origem}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {formatCurrency(lead.valor)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{lead.responsavel}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => setViewingLead(lead)}
                            className="text-blue-600 hover:text-blue-900 p-1 rounded"
                            title="Visualizar"
                          >
                            <Eye size={16} />
                          </button>
                          <button
                            onClick={() => setEditingLead(lead)}
                            className="text-yellow-600 hover:text-yellow-900 p-1 rounded"
                            title="Editar"
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            onClick={() => handleDeleteLead(lead._id)}
                            className="text-red-600 hover:text-red-900 p-1 rounded"
                            title="Excluir"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modais */}
      <LeadForm
        isOpen={showForm}
        onClose={() => setShowForm(false)}
        onSubmit={handleCreateLead}
        title="Cadastrar Novo Lead"
      />

      <LeadForm
        isOpen={!!editingLead}
        onClose={() => setEditingLead(null)}
        onSubmit={handleEditLead}
        initialData={editingLead || undefined}
        title="Editar Lead"
      />

      <LeadDetail
        lead={viewingLead}
        isOpen={!!viewingLead}
        onClose={() => setViewingLead(null)}
        onUpdateStatus={handleStatusUpdate}
      />
    </div>
  )
}

export default CRMDashboard
